/** Preparing images/screenshots and PDFs for upload (client side). */
import { apiUpload } from './api.ts';

export const MAX_FILES = 10;
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const MAX_EDGE = 2560;
const ACCEPT = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'];
export const ACCEPT_ATTR = 'image/png,image/jpeg,image/webp,image/gif,application/pdf';

export interface PendingFile {
  key: string;
  file: Blob;
  name: string;
  kind: 'image' | 'file';
  mime: string;
  width: number | null;
  height: number | null;
  /** Object URL for the thumbnail (images only). */
  preview: string | null;
}

export function acceptable(f: File): boolean {
  return ACCEPT.includes(f.type);
}

async function loadImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Downscale big photos (bandwidth to China is precious). Screenshots (PNG) under 4 MB are kept
 * as-is so small text stays sharp; GIFs are never re-encoded (animation).
 */
export async function prepare(f: File): Promise<PendingFile> {
  const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const name = f.name || (f.type === 'application/pdf' ? 'document.pdf' : `image.${f.type.split('/')[1] ?? 'png'}`);
  if (f.type === 'application/pdf') return { key, file: f, name, kind: 'file', mime: f.type, width: null, height: null, preview: null };
  let blob: Blob = f;
  let mime = f.type;
  let width: number | null = null;
  let height: number | null = null;
  try {
    const img = await loadImage(f);
    width = img.naturalWidth;
    height = img.naturalHeight;
    const big = Math.max(width, height) > MAX_EDGE;
    const keep = f.type === 'image/gif' || (f.type === 'image/png' && f.size <= 4 * 1024 * 1024 && !big);
    if (!keep && (big || f.size > 1.5 * 1024 * 1024)) {
      const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      const g = canvas.getContext('2d')!;
      g.fillStyle = '#fff'; // transparent PNG → JPEG
      g.fillRect(0, 0, canvas.width, canvas.height);
      g.drawImage(img, 0, 0, canvas.width, canvas.height);
      const out = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.86));
      if (out && out.size < f.size) {
        blob = out;
        mime = 'image/jpeg';
        width = canvas.width;
        height = canvas.height;
      }
    }
  } catch {
    /* undecodable here (e.g. HEIC on desktop): the server will refuse it with a clear error */
  }
  return { key, file: blob, name, kind: 'image', mime, width, height, preview: URL.createObjectURL(blob) };
}

export async function upload(conversationId: string, p: PendingFile): Promise<string> {
  const qs = new URLSearchParams({ name: p.name });
  if (p.width) qs.set('width', String(p.width));
  if (p.height) qs.set('height', String(p.height));
  const r = await apiUpload<{ id: string }>(`/api/conversations/${conversationId}/attachments?${qs}`, p.file, p.mime, 120_000);
  return r.id;
}

export const fmtBytes = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} kB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
